// Wave 14+ — Rapha Fit & Compression page (/rapha/fit).
//
// Standalone three-state flow: setup → sweeping → report. Reuses the
// existing useVisionPipeline hook + the body-rotation half of yawSweep
// (Wave 13 / Track A). When the sweep completes we build the body
// visual hull, cross-section it for circumferences (fitMeasurements),
// POST to /fit/sweep, and render the result + the compression view.

import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import { colors } from '../theme';
import { useCurrentUser } from '../services/useCurrentUser';
import { useVisionPipeline } from '../hooks/useVisionPipeline';
import { YawSweep, estimateBodyYawDeg } from '../vision/yawSweep';
import { extractFitMeasurements } from '../vision/fitMeasurements';
import FitReport from './FitReport';

type Phase = 'setup' | 'sweeping' | 'computing' | 'report' | 'failed';

interface FitProfile {
  heightCm: number | null;
  chestCm: number | null;
  waistCm: number | null;
  hipCm: number | null;
  thighCm: number | null;
  shoulderWidthCm: number | null;
  torsoLengthCm: number | null;
  upperArmCm: number | null;
  circumferenceSeries: Array<{ y_cm: number; circumference_cm: number }>;
  recommendedSizes: Record<string, string>;
}

interface Recommendation {
  garmentId: string;
  category: string;
  garmentName: string;
  recommendedSize: string | null;
  score: number;
}

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';
const VISION_WS = process.env.REACT_APP_VISION_WS || '';

export default function RaphaFit() {
  const { t } = useTranslation('rapha-fit');
  const navigate = useNavigate();
  const user = useCurrentUser();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const yawSweepRef = useRef(new YawSweep());

  const [phase, setPhase] = useState<Phase>('setup');
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [prompt, setPrompt] = useState<string | null>(null);
  const [profile, setProfile] = useState<FitProfile | null>(null);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  // Persist the carved hull mesh after the sweep so the CompressionMap
  // can render it in 3D without re-running carving server-side.
  const [hullMesh, setHullMesh] = useState<{
    positions: Float32Array;
    indices: Uint16Array | Uint32Array;
    normals: Float32Array;
  } | null>(null);

  const pipeline = useVisionPipeline({
    videoRef,
    wsUrl: VISION_WS,
    enabled: cameraReady,
  });

  // Camera acquisition. Released on unmount so the next session view
  // can re-acquire cleanly.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setCameraReady(true);
      } catch (e: any) {
        if (cancelled) return;
        if (e?.name === 'NotAllowedError') setCameraError(t('setup.cameraDenied'));
        else if (e?.name === 'NotFoundError') setCameraError(t('setup.cameraUnavailable'));
        else setCameraError(e?.message ?? 'Camera unavailable');
      }
    })();
    return () => {
      cancelled = true;
      const stream = streamRef.current;
      stream?.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drive the body-only sweep state machine on a 12 Hz tick once the
  // user kicks off the sweep. Captures body keyframes when the sweep
  // asks. On completion, transitions to computing → builds hull →
  // POSTs measurements → moves to report.
  useEffect(() => {
    if (phase !== 'sweeping') return;
    const id = setInterval(async () => {
      const sweep = yawSweepRef.current;
      if (sweep.currentPhase() === 'idle' || sweep.currentPhase() === 'waiting') return;
      const lm2 = pipeline.visualization.landmarksRef.current;
      const lmW = pipeline.visualization.worldLandmarksRef.current;
      const bodyYaw = estimateBodyYawDeg(lm2 as any, lmW as any);
      const active = sweep.update({
        now: performance.now(),
        headYawDeg: null,
        bodyYawDeg: bodyYaw,
        cda: null,
      });
      setPrompt(sweep.currentPrompt());

      // Capture body keyframes whenever the sweep asks.
      const slot = sweep.needsBodyKeyframe();
      if (slot && bodyYaw != null) {
        const labelMap = pipeline.visualization.labelMapRef.current;
        const mw = pipeline.visualization.maskWidthRef.current ?? 0;
        const mh = pipeline.visualization.maskHeightRef.current ?? 0;
        const pxPerM = pipeline.calibration?.px_per_m ?? 0;
        if (labelMap && mw > 0 && mh > 0 && pxPerM > 0) {
          let minX = mw, minY = mh, maxX = -1, maxY = -1;
          for (let y = 0; y < mh; y++) {
            const off = y * mw;
            for (let x = 0; x < mw; x++) {
              if (labelMap[off + x] !== 0) {
                if (x < minX) minX = x;
                if (y < minY) minY = y;
                if (x > maxX) maxX = x;
                if (y > maxY) maxY = y;
              }
            }
          }
          const bodyBboxPx: [number, number, number, number] | null =
            maxX >= 0 && maxY >= 0
              ? [minX, minY, maxX - minX + 1, maxY - minY + 1]
              : null;
          sweep.acceptBodyKeyframe({
            yawDeg: bodyYaw,
            labelMap: new Uint8Array(labelMap),
            maskW: mw,
            maskH: mh,
            pxPerM,
            headBboxPx: null,
            bodyBboxPx,
          });
        }
      }

      // Sweep finished — build hull + measurements + POST.
      if (!active && sweep.result_()) {
        clearInterval(id);
        setPhase('computing');
        const bodyKeyframes = sweep.result_()?.bodyKeyframes ?? [];
        if (bodyKeyframes.length < 2) {
          setPhase('failed');
          return;
        }
        try {
          const mod = await import('../vision/visualHull');
          const hull = mod.buildVisualHullBody(bodyKeyframes);
          if (!hull) {
            setPhase('failed');
            return;
          }
          const riderHeightM = pipeline.smplFit?.rider_height_m ?? null;
          const m = extractFitMeasurements(
            hull.occupancy,
            hull.gridDims,
            hull.voxelSize,
            hull.worldOrigin,
            riderHeightM,
          );
          // Stash the hull mesh so CompressionMap can render it in 3D.
          setHullMesh({
            positions: hull.positions,
            indices: hull.indices,
            normals: hull.normals,
          });
          // Upper arm circumference: arms blend into the body hull at
          // single-view voxel resolution, so derive instead from the
          // last world-landmark frame the pipeline saw. Shoulder→elbow
          // distance × an adult anthropometric ratio gives a reasonable
          // estimate. Not as honest as a direct measurement, but better
          // than null.
          const wlm = pipeline.visualization.worldLandmarksRef.current;
          let upperArmCm: number | null = null;
          if (wlm && wlm.length >= 33) {
            const dist = (a: number, b: number) =>
              Math.hypot(wlm[a].x - wlm[b].x, wlm[a].y - wlm[b].y, wlm[a].z - wlm[b].z);
            const lengths: number[] = [];
            if ((wlm[11]?.visibility ?? 0) > 0.5 && (wlm[13]?.visibility ?? 0) > 0.5) {
              lengths.push(dist(11, 13));
            }
            if ((wlm[12]?.visibility ?? 0) > 0.5 && (wlm[14]?.visibility ?? 0) > 0.5) {
              lengths.push(dist(12, 14));
            }
            if (lengths.length) {
              const meanLenM = lengths.reduce((s, v) => s + v, 0) / lengths.length;
              // Adult upper-arm circumference ≈ 0.95 × upper-arm length.
              upperArmCm = meanLenM * 100 * 0.95;
            }
          }
          const dto = {
            heightCm: m.height_cm,
            chestCm: m.chest_cm,
            waistCm: m.waist_cm,
            hipCm: m.hip_cm,
            thighCm: m.thigh_cm,
            shoulderWidthCm: m.shoulder_width_cm,
            torsoLengthCm: m.torso_length_cm,
            upperArmCm,
            circumferenceSeries: m.series,
          };
          const token = localStorage.getItem(TOKEN_KEY);
          const resp = await fetch(`${API}/fit/sweep`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(dto),
          });
          if (!resp.ok) {
            setPhase('failed');
            return;
          }
          const body = await resp.json();
          setProfile(body.profile);
          setRecommendations(body.recommendations ?? []);
          setPhase('report');
        } catch {
          setPhase('failed');
        }
      }
    }, 80);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Non-rapha gate. We don't 403 — show a friendly card.
  if (user.isAuthenticated && user.role !== 'rapha') {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: colors.bg }}>
        <AppHeader />
        <Box sx={{ maxWidth: 640, mx: 'auto', mt: 8, px: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
            {t('gate.title')}
          </Typography>
          <Typography sx={{ color: colors.textMuted, mb: 3 }}>{t('gate.body')}</Typography>
          <Button
            component={RouterLink}
            to="/session"
            variant="contained"
            sx={{
              backgroundColor: colors.brand,
              color: colors.bg,
              fontWeight: 700,
              '&:hover': { backgroundColor: colors.brandBright },
            }}
          >
            {t('gate.cta')}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: colors.bg }}>
      <AppHeader />
      <Box sx={{ maxWidth: 1240, mx: 'auto', px: { xs: 2.5, md: 4 }, py: { xs: 4, md: 6 } }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="overline" sx={{ color: colors.brand }}>
            {t('overline')}
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              fontWeight: 800,
              letterSpacing: '-0.02em',
              mt: 1,
              mb: 1,
            }}
          >
            {t('title')}
          </Typography>
          <Typography sx={{ color: colors.textMuted, maxWidth: 720 }}>{t('subtitle')}</Typography>
        </Box>

        {/* CameraMirror is rendered once and stays mounted across setup
            and sweeping. Phase-specific chrome (Begin button, sweep
            prompt) renders below. Remounting the <video> would discard
            the srcObject we set in the camera-acquisition effect, which
            is why the older split-render version went black on the
            phase transition. */}
        {(phase === 'setup' || phase === 'sweeping') && (
          <Stack spacing={3}>
            <CameraMirror
              videoRef={videoRef}
              ready={cameraReady}
              error={cameraError}
              t={t}
              fillHeight={phase === 'sweeping' ? 520 : 420}
            />
            {phase === 'setup' && (
              <Box>
                <Typography sx={{ color: colors.textMuted, mb: 2 }}>{t('setup.ready')}</Typography>
                <Button
                  disabled={!cameraReady}
                  onClick={() => {
                    yawSweepRef.current.startBodyOnly();
                    setPhase('sweeping');
                  }}
                  variant="contained"
                  size="large"
                  startIcon={<PlayArrowIcon />}
                  sx={{
                    backgroundColor: colors.brand,
                    color: colors.bg,
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    '&:hover': { backgroundColor: colors.brandBright },
                    '&:disabled': { backgroundColor: colors.surfaceElevated, color: colors.textFaint },
                  }}
                >
                  {t('setup.begin')}
                </Button>
              </Box>
            )}
            {phase === 'sweeping' && (
              <Box
                sx={{
                  p: 3,
                  borderRadius: 3,
                  backgroundColor: colors.surface,
                  border: `1px solid ${colors.borderFocus}`,
                  textAlign: 'center',
                }}
              >
                <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.textMuted, mb: 1 }}>
                  {t('sweep.title')}
                </Typography>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: colors.brand }}>
                  {prompt ?? '…'}
                </Typography>
              </Box>
            )}
          </Stack>
        )}

        {phase === 'computing' && (
          <Stack spacing={2} alignItems="center" sx={{ py: 8 }}>
            <CircularProgress sx={{ color: colors.brand }} />
            <Typography sx={{ color: colors.textMuted }}>{t('sweep.computing')}</Typography>
          </Stack>
        )}

        {phase === 'failed' && (
          <Stack spacing={2} alignItems="flex-start" sx={{ py: 4 }}>
            <Typography sx={{ color: colors.danger, fontWeight: 700 }}>
              {t('sweep.failed')}
            </Typography>
            <Button onClick={() => navigate(0)} startIcon={<ArrowBackIcon />} variant="outlined">
              {t('report.remeasure')}
            </Button>
          </Stack>
        )}

        {phase === 'report' && profile && (
          <FitReport
            profile={profile}
            recommendations={recommendations}
            hullMesh={hullMesh}
            onRemeasure={() => navigate(0)}
          />
        )}
      </Box>
    </Box>
  );
}

function CameraMirror({
  videoRef,
  ready,
  error,
  t,
  fillHeight,
}: {
  videoRef: React.RefObject<HTMLVideoElement>;
  ready: boolean;
  error: string | null;
  t: (key: string) => string;
  fillHeight: number;
}) {
  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 4,
        overflow: 'hidden',
        backgroundColor: '#000',
        border: `1px solid ${ready ? colors.borderFocus : colors.border}`,
        boxShadow: ready
          ? `0 0 0 1px ${colors.brandShadow}, 0 24px 56px rgba(106, 255, 69, 0.18)`
          : 'none',
        transition: 'box-shadow 220ms, border-color 220ms',
        height: fillHeight,
      }}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          transform: 'scaleX(-1)',
          display: ready ? 'block' : 'none',
        }}
      />
      {!ready && (
        <Stack
          spacing={2}
          alignItems="center"
          justifyContent="center"
          sx={{ position: 'absolute', inset: 0, color: colors.textMuted, textAlign: 'center', px: 3 }}
        >
          {!error && <CircularProgress size={28} sx={{ color: colors.brand }} />}
          <Typography sx={{ fontSize: '0.875rem', maxWidth: 360 }}>
            {error ?? t('setup.cameraRequesting')}
          </Typography>
        </Stack>
      )}
      {ready && (
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            px: 1.25,
            py: 0.375,
            borderRadius: 999,
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
            backdropFilter: 'blur(12px)',
            border: `1px solid ${colors.borderFocus}`,
            display: 'flex',
            alignItems: 'center',
            gap: 0.625,
          }}
        >
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: colors.brand, boxShadow: `0 0 6px ${colors.brand}` }} />
          <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em', color: colors.text, textTransform: 'uppercase' }}>
            {t('setup.live')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
